import Api from './axiosInstance'
import { API_INVENTORY } from 'utils/constants'

type FetchInventoryParams = {
  limit: number
  page: number
  search?: string
  filters?: any
  barcodeToken?: string | null
  barcode?: string | null
}

const fetchInventoryDetails = ({
  limit,
  page,
  search,
  filters,
  barcodeToken,
  barcode,
}: FetchInventoryParams) => {
  let options: any = {}

  if (search) {
    options.search = search
  }

  if (filters) {
    options = { ...options, ...filters }
  }

  const params = new URLSearchParams({
    limit,
    page,
    ...options,
  })

  // Advance search by barcode: scope the list to the uploaded barcode set
  if (barcodeToken) {
    params.set('barcodeToken', barcodeToken)
  }

  // Advance search by barcode: typed/pasted exact barcodes (comma-separated)
  if (barcode) {
    params.set('barcode', String(barcode))
  }

  return Api.get({
    url: `${API_INVENTORY.INVENTORY}?${params}`,
  })
}

const getInventory = (id: string) => {
  return Api.get({ url: `${API_INVENTORY.INVENTORY}/${id}` })
}

const updateInventory = (id: string, data: any) => {
  return Api.patch({
    url: `${API_INVENTORY.INVENTORY}/${id}`,
    data: data,
  })
}

const updateErpWindowByStores = (data: {
  storeIds: string[]
  allowErpWindowUpdate: boolean
}) => {
  return Api.patch({ url: API_INVENTORY.ERP_WINDOW_UPDATE_BY_STORES, data })
}

const importInventory = async (data: any) => {
  return Api.post({
    url: API_INVENTORY.INVENTORY_IMPORT,
    data: data,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// To Upload a Pricing file in server and update Pricing data
const importErpQtyResetBarcodes = async (data: any) => {
  return Api.post({
    url: API_INVENTORY.ERP_QTY_RESET_BARCODES_IMPORT,
    data: data,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

const downloadInventory = ({
  filters,
  barcodeToken,
  barcode,
}: {
  filters: Record<string, any>
  barcodeToken?: string | null
  barcode?: string | null
}) => {
  const params = new URLSearchParams(filters)

  // Advance search by barcode: scope the export to the uploaded barcode set
  if (barcodeToken) {
    params.set('barcodeToken', barcodeToken)
  }

  // Advance search by barcode: typed/pasted exact barcodes (comma-separated)
  if (barcode) {
    params.set('barcode', String(barcode))
  }

  return Api.get({
    url: `${API_INVENTORY.INVENTORY_EXPORT}?${params}`,
  })
}

// Advance search by barcode: upload a single-column CSV of barcodes and get a
// short-lived token (valid 60 min) that scopes the inventory list/export.
const uploadInventoryBarcodeSearchFile = (file: File) => {
  const formData = new FormData()
  // field name MUST be "file"
  formData.append('file', file)

  return Api.post({
    url: API_INVENTORY.INVENTORY_BARCODE_SEARCH,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

const getStockInventory = ({ filters }: { filters: Record<string, any> }) => {
  const params = new URLSearchParams(filters)
  return Api.get({
    url: `${API_INVENTORY.INVENTORY_STOCK_CHECKER}?${params}`,
  })
}

const downloadErpResetBarcodes = () => {
  return Api.get({
    url: `${API_INVENTORY.ERP_QTY_RESET_BARCODES_EXPORT}`,
  })
}

export {
  fetchInventoryDetails,
  updateInventory,
  updateErpWindowByStores,
  getInventory,
  importInventory,
  downloadInventory,
  importErpQtyResetBarcodes,
  downloadErpResetBarcodes,
  getStockInventory,
  uploadInventoryBarcodeSearchFile,
}
